import "./styles.css";

import { api, ApiError } from "./api/client";
import { webSessionStorageKey } from "./config";
import { connectLiveUpdates } from "./live_updates";
import { parseWebRoute, type WebRoute } from "./routing";
import type { CallsPageSelection } from "./calls_selection";
import type { AppData, NotificationPreferences } from "./types";
import { el } from "./ui/atoms/dom";
import { sidebar } from "./ui/organisms/sidebar";
import { topbar } from "./ui/organisms/topbar";
import { calendarPage } from "./ui/pages/calendar_page";
import { appointmentDetailPage } from "./ui/pages/appointment_detail_page";
import { callsPage } from "./ui/pages/calls_page";
import { customerDetailPage } from "./ui/pages/customer_detail_page";
import { customersPage } from "./ui/pages/customers_page";
import { dashboardPage } from "./ui/pages/dashboard_page";
import { managePage } from "./ui/pages/manage_page";
import { pairingPage } from "./ui/pages/pairing_page";

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("app_root_missing");

let cleanup: (() => void) | null = null;
let data: AppData | null = null;
let stopLiveUpdates: (() => void) | null = null;
let liveRefreshTimer: number | null = null;
let liveRefreshRunning = false;
let liveDataDirty = false;
let livePollTimer: number | null = null;
let callsPageSelection: CallsPageSelection | null = null;

function route(): WebRoute {
  return parseWebRoute(window.location.hash);
}

function mount(node: HTMLElement, nextCleanup?: () => void) {
  cleanup?.();
  cleanup = nextCleanup ?? null;
  app!.replaceChildren(node);
}

async function bootstrap() {
  if (!localStorage.getItem(webSessionStorageKey)) {
    showPairing();
    return;
  }
  mount(el("main", { className: "loading-screen" }, el("div", { className: "spinner" }), el("p", { text: "İşletme verileri yükleniyor…" })));
  try {
    data = await api.loadAppData();
    startLiveUpdates();
    renderApp();
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      showPairing();
      return;
    }
    mount(el("main", { className: "error-screen" }, el("h1", { text: "Veriler yüklenemedi" }), el("p", { text: "Bağlantınızı kontrol edip yeniden deneyin." }), el("button", { className: "button button--primary", text: "Tekrar dene", attrs: { type: "button" }, onClick: () => void bootstrap() })));
  }
}

function showPairing() {
  stopLiveUpdates?.();
  stopLiveUpdates = null;
  if (liveRefreshTimer !== null) window.clearTimeout(liveRefreshTimer);
  if (livePollTimer !== null) window.clearTimeout(livePollTimer);
  liveRefreshTimer = null;
  livePollTimer = null;
  callsPageSelection = null;
  localStorage.removeItem(webSessionStorageKey);
  const view = pairingPage((token) => {
    localStorage.setItem(webSessionStorageKey, token);
    void bootstrap();
  });
  mount(view.element, view.cleanup);
}

function renderApp() {
  if (!data) return;
  const current = route();
  const section = current.name === "appointment"
    ? "calendar"
    : current.name === "customer"
      ? "customers"
      : current.name;
  const organization = data.organizations[0];
  const unread = data.notifications.filter((item) => !item.read).length;
  const meta: Record<WebRoute["name"], [string, string]> = {
    home: ["Genel Bakış", "İşletmenizin bugünkü akışı"],
    calendar: ["Takvim", "Randevuları haftalık görünümde yönetin"],
    calls: ["Görüşmeler", "AI asistanınızın telefon konuşmaları"],
    customers: ["Müşteriler", "Müşteri ve hasta portföyünüz"],
    manage: ["Yönet", "İşletme, asistan ve entegrasyon ayarları"],
    appointment: ["Randevu Detayı", "Randevuyu görüntüleyin, düzenleyin veya iptal edin"],
    customer: ["Müşteri Profili", "Randevu ve görüşme geçmişi"],
  };
  const openAppointment = (id: string) => navigate(`appointment/${encodeURIComponent(id)}`);
  const openCustomer = (id: string) => navigate(`customer/${encodeURIComponent(id)}`);
  const openConversation = (id: string) => {
    callsPageSelection = { groupKey: null, callId: id };
    navigate(`calls/${encodeURIComponent(id)}`);
  };
  const goBack = (fallback: string) => {
    if (window.history.length > 1) window.history.back();
    else navigate(fallback);
  };
  const reloadData = async () => {
    data = await api.loadAppData();
    renderApp();
  };
  const updateNotificationPreferences = async (
    preferences: NotificationPreferences,
  ) => {
    data!.notificationPreferences = await api.updateNotificationPreferences(
      preferences,
    );
    renderApp();
  };
  const pages: Record<WebRoute["name"], () => HTMLElement> = {
    home: () => dashboardPage(data!, openAppointment, openConversation),
    calendar: () => calendarPage(data!, openAppointment),
    calls: () => callsPage(
      data!,
      openAppointment,
      current.id,
      callsPageSelection,
      (selection) => {
        callsPageSelection = selection;
      },
    ),
    customers: () => customersPage(data!, openCustomer),
    manage: () => managePage(data!, updateNotificationPreferences),
    appointment: () => appointmentDetailPage(data!, current.id ?? "", {
      onBack: () => goBack("calendar"),
      onOpenCustomer: openCustomer,
      onChanged: reloadData,
    }),
    customer: () => customerDetailPage(data!, current.id ?? "", {
      onBack: () => goBack("customers"),
      onOpenAppointment: openAppointment,
    }),
  };
  const navigate = (next: string) => {
    const nextHash = `#/${next}`;
    if (window.location.hash === nextHash) {
      liveDataDirty = true;
      scheduleLiveRefresh(0);
      return;
    }
    window.location.hash = `/${next}`;
  };
  const logout = async () => {
    try { await api.logout(); } catch { /* Local logout remains valid. */ }
    localStorage.removeItem(webSessionStorageKey);
    showPairing();
  };
  mount(el("div", { className: "app-shell" }, sidebar(section, organization?.name ?? data.user.displayName, data.user.initials, navigate), el("main", { className: "main-area" }, topbar(meta[current.name][0], meta[current.name][1], unread, () => void logout()), el("div", { className: "page-content" }, pages[current.name]()))));
}

function startLiveUpdates() {
  stopLiveUpdates?.();
  stopLiveUpdates = connectLiveUpdates(() => {
    liveDataDirty = true;
    if (isLiveRoute(route().name)) scheduleLiveRefresh(80);
  });
  scheduleLivePoll();
}

function scheduleLiveRefresh(delay = 140) {
  if (liveRefreshTimer !== null) window.clearTimeout(liveRefreshTimer);
  liveRefreshTimer = window.setTimeout(() => {
    liveRefreshTimer = null;
    void refreshLiveData();
  }, delay);
}

async function refreshLiveData() {
  if (liveRefreshRunning) {
    liveDataDirty = true;
    return;
  }
  liveRefreshRunning = true;
  liveDataDirty = false;
  try {
    data = await api.loadAppData();
    renderApp();
  } catch {
    liveDataDirty = true;
  } finally {
    liveRefreshRunning = false;
    if (liveDataDirty) scheduleLiveRefresh();
    scheduleLivePoll();
  }
}

function scheduleLivePoll() {
  if (livePollTimer !== null) window.clearTimeout(livePollTimer);
  livePollTimer = null;
  if (!data || !isLiveRoute(route().name)) return;
  const hasLiveCall = data.conversations.some((conversation) =>
    ["connected", "in_progress", "ringing", "queued", "forwarding"]
      .includes(conversation.status)
  );
  livePollTimer = window.setTimeout(() => {
    liveDataDirty = true;
    scheduleLiveRefresh(0);
  }, hasLiveCall ? 1200 : 5000);
}

window.addEventListener("hashchange", () => {
  if (!data) return;
  // Every destination starts from a fresh tenant-scoped snapshot instead of
  // rendering the previous screen's in-memory data.
  liveDataDirty = true;
  scheduleLiveRefresh(0);
});

function isLiveRoute(name: WebRoute["name"]) {
  return name === "home" || name === "calls";
}
void bootstrap();
